import { StyleValue } from 'vue';
import { OverviewDataType } from './pages/useInventoryDashboardStore/types';
import { MetricTypes } from './domains/entities/Dashboard';

export const INVENTORY_DASHBOARD_PATH = '/inventory-dashboard';

/**
 * ダッシュボードの表示項目用interface
 */
export type DashboardGeneralSectionItem = {
  key: string;
  label: string;
  valueKey: OverviewDataType[];
  unit: string[];
  to: string;
  icon?: IconProps;
};

/**
 * ダッシュボードの表示項目のicon情報
 */
export type IconProps = {
  name: string;
  variant?: 'filled' | 'outlined' | 'round';
  style?: StyleValue;
};

export const DASHBOARD_GENERAL_SECTION_ITEM = [
  {
    key: 'inventory',
    label: '在庫数量と回転率',
    valueKey: ['totalCurrentInventoryAmount', 'averageTurnover'],
    unit: ['', '月'],
    to: '/inventory-dashboard/stock',
  },
  {
    key: 'averageForecastAccuracy',
    label: '内示正答率',
    valueKey: ['averageForecastAccuracy'],
    unit: ['%'],
    to: '/inventory-dashboard/forecast-accuracy',
  },
  {
    key: 'averageKpiArchievementRate',
    label: '在庫回転率のKPI達成率',
    valueKey: [],
    unit: [],
    to: '/inventory-dashboard/kpi-history',
  },
  {
    key: 'warningItems',
    label: '要注意リスト',
    valueKey: ['warningItemsCount'],
    unit: ['inventoryDashboard:件'],
    to: '/inventory-dashboard/alerts?severity=warning',
    icon: {
      name: 'warning_amber',
      variant: 'round',
      style: { color: '#FB8C00', fontSize: '29px' },
    },
  },
  {
    key: 'criticalItems',
    label: '危険リスト',
    valueKey: ['criticalItemsCount'],
    unit: ['inventoryDashboard:件'],
    to: '/inventory-dashboard/alerts?severity=critical',
    icon: {
      name: 'warning_amber',
      variant: 'round',
      style: { color: '#D50000', fontSize: '29px' },
    },
  },
] as const satisfies ReadonlyArray<DashboardGeneralSectionItem>;

export const FILTER_OPTIONS = {
  ANNOUNCE_ACCURACY: ['前月の正答率', '直近3ヶ月平均', '直近6ヶ月平均', '直近12ヶ月平均'],
};

export const PageType = {
  Stock: 'stock',
  ForecastAccuracy: 'forecastAccuracy',
  Critical: 'critical',
  Warning: 'warning',
} as const;

/**
 * 詳細ページの種類
 */
export type PageType = (typeof PageType)[keyof typeof PageType];

/**
 * ヘッダーのオプションタイプ
 */
export type HeaderOption = {
  key: string;
  width: number;
};

export const PAGE_CONFIG: Record<PageType, { title: string; desc?: string; icon?: IconProps }> = {
  [PageType.Stock]: {
    title: '在庫数量と回転率',
    desc: '前月の取引ごとの在庫数量と在庫回転率を一覧確認できます',
  },
  [PageType.ForecastAccuracy]: {
    title: '内示正答率一覧（低い順）',
    desc: '内示正答率が低い商品から順に表示しています（前月実績）',
  },
  [PageType.Critical]: {
    title: '危険リスト',
    desc: 'すぐに対応が必要な取引の一覧です。',
    icon: {
      name: 'warning_amber',
      variant: 'round',
      style: { color: '#D50000', fontSize: '32px' },
    },
  },
  [PageType.Warning]: {
    title: '要注意リスト',
    desc: '確認が必要な取引の一覧です。',
    icon: {
      name: 'warning_amber',
      variant: 'round',
      style: { color: '#FB8C00', fontSize: '32px' },
    },
  },
};

export const DETAIL_HEADER_OPTION: Record<PageType, HeaderOption[]> = {
  [PageType.Stock]: [
    { key: 'checkbox', width: 52 },
    { key: 'detail', width: 56 },
    { key: 'warehouseNumber', width: 160 },
    { key: 'warehouse', width: 160 },
    { key: 'supplierNumber', width: 160 },
    { key: 'supplierName', width: 160 },
    { key: 'businessNumber', width: 160 },
    { key: 'businessName', width: 160 },
    { key: 'package', width: 74 },
    { key: 'manufacturer', width: 160 },
    { key: 'aggregateBusinessResponsibleUsers', width: 160 },
    { key: 'latestInventoryAmount', width: 128 },
    { key: 'lastMonthEndInventoryAmount', width: 128 },
    { key: 'lastMonthSalesResultAmount', width: 128 },
    { key: 'turnover', width: 128 },
    { key: 'headerModify', width: 57 },
  ],
  [PageType.ForecastAccuracy]: [
    { key: 'checkbox', width: 52 },
    { key: 'detail', width: 56 },
    { key: 'warehouseNumber', width: 160 },
    { key: 'warehouse', width: 160 },
    { key: 'supplierNumber', width: 160 },
    { key: 'supplierName', width: 160 },
    { key: 'businessNumber', width: 160 },
    { key: 'businessName', width: 160 },
    { key: 'package', width: 74 },
    { key: 'customerNumber', width: 160 },
    { key: 'customerName', width: 160 },
    { key: 'salesDestinationNumber', width: 160 },
    { key: 'salesDestinationName', width: 160 },
    { key: 'endUser', width: 160 },
    { key: 'baseBusinessResponsibleUsers', width: 160 },
    { key: 'forecastForReceiptName', width: 160 },
    { key: 'forecastForReceiptResponsibleUsers', width: 160 },
    { key: 'salesResultAmount', width: 128 },
    { key: 'customerForecastAmount', width: 128 },
    { key: 'forecastAccuracy', width: 128 },
    { key: 'headerModify', width: 57 },
  ],
  [PageType.Critical]: [
    { key: 'checkbox', width: 52 },
    { key: 'detail', width: 56 },
    { key: 'warehouseNumber', width: 160 },
    { key: 'warehouse', width: 160 },
    { key: 'supplierNumber', width: 160 },
    { key: 'supplierName', width: 160 },
    { key: 'businessNumber', width: 160 },
    { key: 'businessName', width: 160 },
    { key: 'package', width: 74 },
    { key: 'manufacturer', width: 160 },
    { key: 'aggregateBusinessResponsibleUsers', width: 160 },
    { key: 'reason', width: 520 },
    { key: 'headerModify', width: 57 },
  ],
  [PageType.Warning]: [
    { key: 'checkbox', width: 52 },
    { key: 'detail', width: 56 },
    { key: 'warehouseNumber', width: 160 },
    { key: 'warehouse', width: 160 },
    { key: 'supplierNumber', width: 160 },
    { key: 'supplierName', width: 160 },
    { key: 'businessNumber', width: 160 },
    { key: 'businessName', width: 160 },
    { key: 'package', width: 74 },
    { key: 'manufacturer', width: 160 },
    { key: 'aggregateBusinessResponsibleUsers', width: 160 },
    { key: 'reason', width: 520 },
    { key: 'headerModify', width: 57 },
  ],
};

export const LIST_VIEW_BASE_HEADER_KEYS: readonly { key: string; width: number }[] = [
  { key: 'checkbox', width: 52 },
  { key: 'detail', width: 56 },
  { key: 'warehouseNumber', width: 160 },
  { key: 'warehouse', width: 160 },
  { key: 'supplierNumber', width: 160 },
  { key: 'supplierName', width: 160 },
  { key: 'businessNumber', width: 160 },
  { key: 'businessName', width: 160 },
  { key: 'package', width: 74 },
];

export const LIST_VIEW_AGG_BASE_HEADER_KEYS: readonly { key: string; width: number }[] = [
  { key: 'manufacturer', width: 160 },
  { key: 'aggregateBusinessResponsibleUsers', width: 160 },
];

export const LIST_VIEW_BIZ_BASE_HEADER_KEYS: readonly { key: string; width: number }[] = [
  { key: 'customerNumber', width: 160 },
  { key: 'customerName', width: 160 },
  { key: 'salesDestinationNumber', width: 160 },
  { key: 'salesDestinationName', width: 160 },
  { key: 'endUser', width: 160 },
  { key: 'baseBusinessResponsibleUsers', width: 160 },
];

export const LIST_VIEW_CUSTOM_HEADER_KEYS: Record<string, readonly { key: string; width: number }[]> = {
  [MetricTypes.Arrival]: [{ key: 'arrival', width: 128 }],
  [MetricTypes.CustomerForecast]: [{ key: 'customerForecast', width: 128 }],
  [MetricTypes.ForecastAccuracy]: [
    { key: 'salesResult', width: 128 },
    { key: 'customerForecast', width: 128 },
    { key: 'forecastAccuracy', width: 128 },
  ],
  [MetricTypes.Inventory]: [{ key: 'endInventory', width: 128 }],
  [MetricTypes.OrderAmount]: [{ key: 'orderAmount', width: 128 }],
  [MetricTypes.SalesResult]: [{ key: 'salesResult', width: 128 }],
  [MetricTypes.SupplierForecast]: [{ key: 'supplierForecast', width: 128 }],
  [MetricTypes.Turnover]: [
    { key: 'turnover', width: 128 },
    { key: 'salesResult', width: 128 },
    { key: 'customerForecast', width: 128 },
    { key: 'endInventory', width: 128 },
  ],
};

export const DEFAULT_DISPLAY_MONTH_PERIOD_FROM = -12;
export const DEFAULT_DISPLAY_MONTH_PERIOD_TO = 6;

export const LIST_VIEW_SECTION_INJECTION_KEY = 'inventoryDashboardListTableWidthInfo' as const;
